import React, { useState, FormEvent, useEffect } from "react";

import classNames from "classnames";
import Link from "next/link";
import { style } from "wavesurfer.js/src/util";

import { ProductData } from "data/product/products";
import { StorefrontData } from "data/storefront";
import { AnimatedSpinner, ArrowRightIcon, Button } from "studio-design-system";

import { TEMPLATE_PRODUCT_DRAFT, TEMPLATE_STOREFRONT_DRAFT } from "~/api/creatorApi";
import { ApiStatus } from "~/api/fetchStudioApi";
import {
  getAiCoachResponse,
  getAiClassSuggestion,
  OptionalPromptParameters,
} from "~/api/sfCoachApi";
import { AutoResizableTextArea } from "~/components/AICoach/AutoResizableTextArea";
import { CreatorCheck } from "~/components/common/CreatorInfo/CreatorCheck";
import { ProfilePic } from "~/components/common/ProfilePic";
import { SelectableDropdown } from "~/components/common/SelectableDropdown/SelectableDropdown";
import { useCreatorEditing, CreatorSavingType, errorBorderStyle } from "~/hooks/useCreatorEditing";

import * as styles from "./AISuggestionsPanel.css";

export const AISuggestionsPanel = ({
  product,
  storefrontData,
  isOpen,
}: {
  product: ProductData;
  storefrontData: StorefrontData;
  isOpen: boolean;
}): React.ReactElement => {
  const { fieldsTexts, fieldsOnError, isAISuggestionsShown, handleTextFieldUpdate } =
    useCreatorEditing();

  const creatorName = storefrontData.creatorName;
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [suggestionText, setSuggestionText] = useState("");
  const sectionsToFill = [
    "Title",
    "Short description",
    "Short bio",
    "Class trailer",
    "Skills",
    "Class overview title",
    "Class overview description",
    "Apply what you'll learn section title",
    "Apply what you'll learn section description",
    "Who Is This For section",
  ];

  interface DataObject {
    [key: string]: {
      key: string;
      type: string;
    };
  }
  const mapSectionsToFillFriendlyToKey: DataObject = {
    Title: { key: "title", type: CreatorSavingType.PRODUCT },
    "Short description": { key: "description", type: CreatorSavingType.PRODUCT },
    "Short bio": { key: "shortBio", type: CreatorSavingType.STOREFRONT },
    "Longer bio": { key: "storefrontDescription", type: CreatorSavingType.STOREFRONT },
    "Class trailer": { key: "trailerScript", type: CreatorSavingType.PRODUCT },
    Skills: { key: "skills", type: CreatorSavingType.PRODUCT },
    "Class overview title": { key: "overviewSectionTitle", type: CreatorSavingType.PRODUCT },
    "Class overview description": {
      key: "overviewSectionDescription",
      type: CreatorSavingType.PRODUCT,
    },
    "Apply what you'll learn section title": {
      key: "exampleMediaSectionTitle",
      type: CreatorSavingType.PRODUCT,
    },
    "Apply what you'll learn section description": {
      key: "exampleMediaSectionDescription",
      type: CreatorSavingType.PRODUCT,
    },
    "Who Is This For section": { key: "whoIsThisForSection", type: CreatorSavingType.PRODUCT },
  };

  const [sectionSelected, setSectionSelected] = useState("title");
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);

  const isCurrentValueNotDefault = (value: string, key: string, type: string) => {
    if (type === "product") {
      return (
        value && value.length > 0 && value !== TEMPLATE_PRODUCT_DRAFT[key as keyof ProductData]
      );
    } else if (type === "storefront") {
      return (
        value &&
        value.length > 0 &&
        value !== TEMPLATE_STOREFRONT_DRAFT[key as keyof StorefrontData]
      );
    }
    return false;
  };
  const getContextInfo = () => {
    let contextInfo = "";

    if (isCurrentValueNotDefault(storefrontData.creatorName, "creatorName", "storefront")) {
      contextInfo += "\n Instructor's name: " + fieldsTexts["creatorName"];
    }

    if (isCurrentValueNotDefault(storefrontData.shortBio, "shortBio", "storefront")) {
      contextInfo += "\n Instructor's short bio: " + fieldsTexts["shortBio"];
    }

    if (isCurrentValueNotDefault(storefrontData.description, "description", "storefront")) {
      contextInfo += "\n Instructor's longer bio: " + fieldsTexts["storefrontDescription"];
    }

    if (isCurrentValueNotDefault(product.title, "title", "product")) {
      contextInfo += "\n Class potential title: " + fieldsTexts["title"];
    }

    if (isCurrentValueNotDefault(product.title, "description", "product")) {
      contextInfo += "\n Class potential description: " + fieldsTexts["description"];
    }

    const defaultSkillText = "Enter a specific topic that students will learn in your class here";
    if (product && product.skills) {
      let realSkills = product.skills.filter((skill) => skill !== defaultSkillText);
      if (realSkills.length > 0) {
        contextInfo += "Some skills learned in class: " + realSkills.join(", ");
      }
    }
    // TODO: see if adding more fields here actually helps.

    return contextInfo;
  };

  const handleAddSuggestion = () => {

    if (sectionSelected === "") {
      setSuggestionText("Please select a section to generate suggestions for.");
      return;
    }

    if ("Skills" === sectionSelected) {
      alert(
        "Skills automatic saving is not supported yet. Please copy and paste the suggestions manually.",
      );
      return;
    }

    if ("Who Is This For section" === sectionSelected) {
      alert(
        "Who Is This For section automatic saving is not supported yet. Please copy and paste the suggestion manually.",
      );
      return;
    }
    handleTextFieldUpdate(
      suggestionText,
      mapSectionsToFillFriendlyToKey[sectionSelected].key as string,
      mapSectionsToFillFriendlyToKey[sectionSelected].type as CreatorSavingType,
    );


    //handleTextChange()
  };

  const handleGenerateSuggestion = async () => {


    if (sectionSelected === "") {
      setSuggestionText("Please select a section to generate suggestions for.");
      return;
    }
    let contextInfo = getContextInfo();

    setIsLoadingSuggestion(true);
    setSuggestionText("");
    const response = await getAiClassSuggestion(
      sectionSelected,
      contextInfo,
      additionalInstructions,
    );


    setIsLoadingSuggestion(false);

    if (response.response) {
      const fullShown = "\n" + response.response;

      // setPromptResponse(fullShown);
      setSuggestionText(response.response);
    } else {
      const errorMessage = "There was an error with the request. Please modify it and try again.";
      setSuggestionText(errorMessage);
    }
  };

  const onAdditionalInstructionsChange = (text: string) => {

    setAdditionalInstructions(text);
  };

  useEffect(() => {

  }, [isAISuggestionsShown, isOpen]);



  const handleSectionSelect = (section: string) => {

    setSectionSelected(section);
  };

  const handleSuggestionTextChange = (text: string) => {
    setSuggestionText(text);
  };

  return (
    <div
      className={classNames(
        styles.panelContainer,
        isAISuggestionsShown ? styles.panelOpen.open : styles.panelOpen.closed,
      )}
    >
      <h1 className={styles.title}>Studio AI Suggestions</h1>
      <div className={styles.description}>
        Suggestions will consider the information you have filled to generate suggestions. The more
        sections you have filled, the more relevant the suggestions will be. It will ignore the
        default values.
      </div>

      <div className={styles.suggestionSection}>
        <div className={styles.sectionTitleContainer}>
          <h3 className={styles.sectionTitle}>Section:</h3>
          <SelectableDropdown
            selectableValues={sectionsToFill}
            handleSelect={handleSectionSelect}
          />
        </div>
        <AutoResizableTextArea
          onTextChange={onAdditionalInstructionsChange}
          defaultValue={""}
          placeholder="Optional: additional instructions. E.g. “make it sound more appealing to advanced users”, or “make it sound friendlier”.  Leave blank for default suggestion. "
          style={{
            minHeight: "100px",
            minWidth: "300px",
            marginTop: "6px",
            marginBottom: "16px",
          }}
        />
        <Button variant="secondary" onClick={handleGenerateSuggestion}>
          Generate suggestion
        </Button>
      </div>
      <div className={styles.suggestionSection}>
        <div className={styles.sectionTitleContainer}>
          <h3 className={styles.sectionTitle}>Suggestion:</h3>
          {isLoadingSuggestion && <AnimatedSpinner />}
        </div>
        <AutoResizableTextArea
          defaultValue={""}
          placeholder=""
          overrideText={suggestionText}
          onTextChange={handleSuggestionTextChange}
          style={{
            minHeight: "200px",
            minWidth: "300px",
            marginTop: "6px",
            marginBottom: "16px",
          }}
        />
        <Button variant="secondary" onClick={handleAddSuggestion}>
          Add suggestion to section
        </Button>
      </div>
    </div>
  );
};
